/**
 * Today's Focus hero card — UI Wave B.
 *
 * The single most important "what to do now" card. Replaces visual noise from
 * the wall of 6+ stacked dashboard cards. Picks the highest-value next action:
 *   1. If exam in <=14 days → "Crunch mode: 80 cards today"
 *   2. If due reviews > 0 → "Review N due cards"
 *   3. If trouble cards exist → "Attack 3 trouble cards"
 *   4. Else → "Start a Smart Review session"
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Flame, Target, Clock, ArrowRight } from "lucide-react";

export default function TodayFocusCard() {
  const [load, setLoad] = useState(null);
  const [trouble, setTrouble] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/student/daily-load").catch(() => null),
      api.get("/student/trouble-cards?limit=1").catch(() => null),
    ]).then(([d, t]) => {
      if (cancelled) return;
      setLoad(d?.data || null);
      setTrouble(t?.data || null);
    });
    return () => { cancelled = true; };
  }, []);

  if (!load) return null;

  const examCrunch = load.mode === "exam-crunch";
  const remaining = load.remaining_today || 0;
  const cap = load.recommended_cap || 0;
  const done = load.answered_today || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((done / cap) * 100)) : 0;
  const hasTrouble = (trouble?.trouble_cards || []).length > 0;

  // Pick the headline action
  let headline, sub, cta, ctaRoute, Icon, gradient;
  if (examCrunch && remaining > 0) {
    Icon = Flame;
    headline = `${load.days_to_exam} ${load.days_to_exam === 1 ? "day" : "days"} to exam · crunch mode`;
    sub = `${remaining} cards left today. Hit the cap to peak on exam day.`;
    cta = "Crunch session";
    ctaRoute = "/student/smart-review";
    gradient = "from-rose-500 via-orange-500 to-amber-500";
  } else if (remaining > 0) {
    Icon = Target;
    headline = `Review ${remaining} due cards`;
    sub = done > 0 ? `${done} done · ${pct}% of today's cap` : `Today's plan: ${cap} cards`;
    cta = "Start Smart Review";
    ctaRoute = "/student/smart-review";
    gradient = "from-brand-700 via-brand-600 to-blue-500";
  } else if (hasTrouble) {
    Icon = Sparkles;
    headline = `Attack your trouble cards`;
    sub = `${trouble.trouble_cards.length} card${trouble.trouble_cards.length === 1 ? "" : "s"} keep tripping you up. Time to break the streak of lapses.`;
    cta = "Quick fix";
    ctaRoute = "/student/smart-review";
    gradient = "from-amber-500 via-orange-500 to-rose-500";
  } else {
    Icon = Sparkles;
    headline = "All caught up — nice work";
    sub = "No reviews due. Try a fresh Smart Review to learn something new.";
    cta = "Start a session";
    ctaRoute = "/student/smart-review";
    gradient = "from-emerald-500 via-teal-500 to-blue-500";
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br p-5 sm:p-7 text-white shadow-xl"
      style={{ backgroundImage: `linear-gradient(135deg, var(--tw-gradient-from), var(--tw-gradient-via), var(--tw-gradient-to))` }}
      data-testid="today-focus-card"
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} pointer-events-none`} />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(circle at 100% 0%, rgba(255,255,255,0.35) 0, transparent 50%), radial-gradient(circle at 0% 100%, rgba(0,0,0,0.25) 0, transparent 50%)",
        }}
      />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/80 mb-1">
          Today's focus
        </div>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight leading-tight">
          {headline}
        </h2>
        <p className="text-sm text-white/85 mt-2 leading-relaxed max-w-xl">{sub}</p>

        <div className="flex items-center gap-3 mt-5 flex-wrap">
          <Button
            type="button"
            onClick={() => nav(ctaRoute)}
            className="h-11 px-5 bg-white text-slate-900 hover:bg-white/90 active:scale-[0.97] font-bold rounded-lg shadow-lg"
            data-testid="today-focus-cta"
          >
            <Icon className="w-4 h-4 mr-2" />
            {cta}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          {cap > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-white/85 font-medium">
              <Clock className="w-3.5 h-3.5" />
              <span className="tabular-nums">{done}/{cap}</span>
              <span>cards today</span>
            </div>
          )}
        </div>

        {/* Subtle progress bar at the bottom */}
        {cap > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15">
            <div
              className="h-full bg-white/70 transition-all duration-700"
              style={{ width: `${pct}%` }}
              data-testid="today-focus-progress"
            />
          </div>
        )}
      </div>
    </div>
  );
}
